import type { Document } from './document.ts';
import type { GridTrack, NodeData, NodeId, NodeProps, Op, Rect, Vec2 } from './types.ts';
import { isFrameLike } from './defaults.ts';
import { measureTextNode, fitStickyFontSize, MIND_PADDING } from './text.ts';
import { localTransform, transformRect, unionRect, apply as applyMat, invert, rectCenter } from './geometry.ts';

/**
 * A copy-on-write view over the document used while computing derived layout.
 * Reads see pending writes; at the end the pending writes become 'set' ops.
 */
class Overlay {
  pending = new Map<NodeId, NodeProps>();
  constructor(readonly doc: Document) {}
  get(id: NodeId): NodeData {
    const base = this.doc.must(id);
    const p = this.pending.get(id);
    return p ? ({ ...base, ...p } as NodeData) : base;
  }
  set(id: NodeId, props: NodeProps) {
    const cur = this.get(id) as unknown as Record<string, unknown>;
    const diff: Record<string, unknown> = {};
    let any = false;
    for (const [k, v] of Object.entries(props)) {
      const old = cur[k];
      if (typeof v === 'number' && typeof old === 'number' ? Math.abs(v - old) > 1e-6 : JSON.stringify(v) !== JSON.stringify(old)) {
        diff[k] = v;
        any = true;
      }
    }
    if (!any) return;
    this.pending.set(id, { ...this.pending.get(id), ...(diff as NodeProps) });
  }
  children(id: NodeId): NodeData[] {
    return this.doc.children(id).map((c) => this.get(c.id));
  }
  ops(): Op[] {
    const out: Op[] = [];
    for (const [id, props] of this.pending) {
      const base = this.doc.must(id) as unknown as Record<string, unknown>;
      const diff: Record<string, unknown> = {};
      let any = false;
      for (const [k, v] of Object.entries(props)) {
        if (JSON.stringify(base[k]) !== JSON.stringify(v)) {
          diff[k] = typeof v === 'number' ? round(v) : v;
          any = true;
        }
      }
      if (any) out.push({ op: 'set', id, props: diff as NodeProps });
    }
    return out;
  }
}

function round(v: number): number {
  return Math.round(v * 1000) / 1000;
}

export function isAutoLayout(n: NodeData | undefined): boolean {
  return !!n && isFrameLike(n.type) && !!n.layoutMode && n.layoutMode !== 'NONE';
}

/** How far the frame's stroke reaches inward on each side [top, right, bottom, left], when strokes count in layout. */
function strokeInset(f: NodeData): [number, number, number, number] {
  if (!f.strokesIncludedInLayout || !f.strokes?.some((p) => p.visible !== false)) return [0, 0, 0, 0];
  const k = f.strokeAlign === 'OUTSIDE' ? 0 : f.strokeAlign === 'CENTER' ? 0.5 : 1;
  const w = f.strokeWeights ?? [f.strokeWeight, f.strokeWeight, f.strokeWeight, f.strokeWeight];
  return [w[0] * k, w[1] * k, w[2] * k, w[3] * k];
}

function inFlow(n: NodeData): boolean {
  return n.visible && n.layoutPositioning !== 'ABSOLUTE';
}

const LAYOUT_KEYS = new Set([
  'x', 'y', 'width', 'height', 'rotation', 'visible', 'parent', 'index', 'mindParent', 'mindMap',
  'layoutMode', 'layoutWrap', 'itemSpacing', 'counterAxisSpacing', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 'strokesIncludedInLayout', 'strokeWeight', 'strokeWeights', 'strokeAlign',
  'primaryAxisAlign', 'counterAxisAlign', 'layoutSizingH', 'layoutSizingV', 'layoutPositioning',
  'gridColumns', 'gridRows', 'gridColumn', 'gridRow', 'gridColumnSpan', 'gridRowSpan',
  'minWidth', 'maxWidth', 'minHeight', 'maxHeight',
  'characters', 'fontFamily', 'fontSize', 'fontWeight', 'italic', 'lineHeight', 'letterSpacing', 'textAutoResize', 'textTruncation', 'maxLines', 'textCase',
  'connectorStart', 'connectorEnd', 'connectorType', 'strokeWeight',
]);

/**
 * Compute derived-layout ops after a set of nodes changed.
 * `changed` = ids touched by the transaction (including deleted ids' former parents).
 */
export function computeLayout(doc: Document, changed: Iterable<NodeId>, keys?: Set<string>): Op[] {
  if (keys && keys.size && ![...keys].some((k) => LAYOUT_KEYS.has(k))) return [];
  const ov = new Overlay(doc);
  const ids = [...changed].filter((id) => doc.has(id));
  if (!ids.length) return [];

  // 1. Text auto resize & sticky font fitting for directly changed text nodes.
  for (const id of ids) {
    const n = ov.get(id);
    if (n.type === 'TEXT') fitText(ov, n.id);
    if (n.type === 'STICKY' && (n.pluginData?.autoFont ?? 'true') === 'true') {
      ov.set(n.id, { fontSize: fitStickyFontSize(n) });
    }
    if (n.mindParent || n.mindMap) fitMindTopic(ov, n.id);
  }

  // 2. Auto layout: find the top-most auto-layout ancestor chain for each change.
  const roots = new Set<NodeId>();
  for (const id of ids) {
    let top: NodeId | null = null;
    let cur: NodeData | undefined = doc.get(id);
    while (cur) {
      if (isAutoLayout(ov.get(cur.id))) top = cur.id;
      const p = doc.get(cur.parent);
      if (!p || !isAutoLayout(ov.get(p.id))) break;
      cur = p;
    }
    if (top) roots.add(top);
  }
  for (const r of roots) layoutFrame(ov, r, 0);

  // 3. Groups shrink-wrap their children.
  const groups = new Set<NodeId>();
  for (const id of [...ids, ...ov.pending.keys()]) {
    for (const a of doc.ancestors(id)) {
      if (a.type !== 'GROUP') break;
      groups.add(a.id);
    }
    if (doc.get(id)?.type === 'GROUP') groups.add(id);
  }
  // deepest first
  [...groups]
    .sort((a, b) => doc.ancestors(b).length - doc.ancestors(a).length)
    .forEach((g) => fitGroup(ov, g));

  // 4. Mind maps: lay out every tree a change touched.
  const trees = new Set<NodeId>();
  for (const id of ids) {
    const r = mindRootOf(doc, id, (x) => ov.get(x));
    if (r) trees.add(r);
  }
  for (const r of trees) layoutMindTree(ov, r);

  // 5. Connectors attached to moved nodes.
  updateConnectors(doc, ov, new Set([...ids, ...ov.pending.keys()]));

  return ov.ops();
}

// ---------------------------------------------------------------------------
// Mind maps: topics hug their text; branches form a tidy tree from the central topic

const MIND_HGAP = 56;
const MIND_VGAP = 14;
const MIND_MAX_TEXT = 240;

function fitMindTopic(ov: Overlay, id: NodeId) {
  const n = ov.get(id);
  const probe = { ...n, type: 'TEXT', textAutoResize: 'WIDTH_AND_HEIGHT' } as NodeData;
  let size = measureTextNode(probe);
  if (size.width > MIND_MAX_TEXT) size = measureTextNode({ ...probe, textAutoResize: 'HEIGHT', width: MIND_MAX_TEXT });
  ov.set(id, { width: Math.max(48, Math.ceil(size.width) + MIND_PADDING * 2), height: Math.ceil(size.height) + MIND_PADDING * 2 });
}

/** The central topic of the mind map `id` belongs to (`get` reads pending layout state). */
export function mindRootOf(doc: Document, id: NodeId, get: (id: NodeId) => NodeData = (x) => doc.must(x)): NodeId | null {
  if (!doc.has(id)) return null;
  let n = get(id);
  for (let i = 0; i < 200; i++) {
    if (n.mindMap) return n.id;
    if (!n.mindParent || !doc.has(n.mindParent)) return null;
    n = get(n.mindParent);
  }
  return null;
}

/** A topic's branches in order (siblings are ordered by their layer order). */
export function mindChildren(doc: Document, id: NodeId): NodeData[] {
  const n = doc.get(id);
  if (!n?.parent) return [];
  return doc.children(n.parent).filter((c) => c.mindParent === id);
}

/** A topic and everything that branches from it. */
export function mindSubtree(doc: Document, id: NodeId): NodeId[] {
  const out = [id];
  for (let i = 0; i < out.length; i++) for (const c of mindChildren(doc, out[i])) out.push(c.id);
  return out;
}

/** Re-lay out whole mind maps (e.g. after a branch was deleted). */
export function mindLayoutOps(doc: Document, roots: NodeId[]): Op[] {
  const ov = new Overlay(doc);
  for (const r of roots) if (doc.has(r)) layoutMindTree(ov, r);
  updateConnectors(doc, ov, new Set(ov.pending.keys()));
  return ov.ops();
}

function layoutMindTree(ov: Overlay, rootId: NodeId) {
  const doc = ov.doc;
  const heights = new Map<NodeId, number>();
  const kids = (id: NodeId) => mindChildren(doc, id).map((c) => ov.get(c.id));
  const subtree = (id: NodeId): number => {
    let h = heights.get(id);
    if (h !== undefined) return h;
    const ks = kids(id);
    const sum = ks.reduce((s, k) => s + subtree(k.id), 0) + MIND_VGAP * Math.max(0, ks.length - 1);
    h = Math.max(ov.get(id).height, sum);
    heights.set(id, h);
    return h;
  };
  // place a topic with its near edge at x (dir 1 = grows right, -1 = grows left), centred on cy
  const place = (id: NodeId, x: number, cy: number, dir: 1 | -1) => {
    const n = ov.get(id);
    const nx = dir > 0 ? x : x - n.width;
    ov.set(id, { x: nx, y: cy - n.height / 2 });
    branch(kids(id), dir > 0 ? nx + n.width + MIND_HGAP : nx - MIND_HGAP, cy, dir);
  };
  const branch = (ks: NodeData[], x: number, cy: number, dir: 1 | -1) => {
    const total = ks.reduce((s, k) => s + subtree(k.id), 0) + MIND_VGAP * Math.max(0, ks.length - 1);
    let y = cy - total / 2;
    for (const k of ks) {
      const h = subtree(k.id);
      place(k.id, x, y + h / 2, dir);
      y += h + MIND_VGAP;
    }
  };
  const root = ov.get(rootId);
  const cy = root.y + root.height / 2;
  const all = kids(rootId);
  const both = root.mindMap?.layout === 'BOTH';
  branch(both ? all.filter((_, i) => i % 2 === 0) : all, root.x + root.width + MIND_HGAP, cy, 1);
  if (both) branch(all.filter((_, i) => i % 2 === 1), root.x - MIND_HGAP, cy, -1);
}

function fitText(ov: Overlay, id: NodeId) {
  const n = ov.get(id);
  if (n.textAutoResize === 'NONE') return;
  const size = measureTextNode(n);
  // auto-sized text boxes snap to whole pixels, as Figma's do (36px Inter: 43.56 → 44 tall)
  const props: NodeProps = { height: Math.round(size.height) };
  if (n.textAutoResize === 'WIDTH_AND_HEIGHT') props.width = Math.max(Math.round(size.width), size.width > 0 ? 1 : 0);
  ov.set(id, props);
}

// ---------------------------------------------------------------------------
// Auto layout (flexbox-like, with HUG / FIXED / FILL sizing)
// ---------------------------------------------------------------------------

function sizing(n: NodeData, axis: 'H' | 'V'): 'FIXED' | 'HUG' | 'FILL' {
  const s = axis === 'H' ? n.layoutSizingH : n.layoutSizingV;
  if (s) return s;
  if (n.type === 'TEXT') {
    if (n.textAutoResize === 'WIDTH_AND_HEIGHT') return 'HUG';
    if (n.textAutoResize === 'HEIGHT' && axis === 'V') return 'HUG';
  }
  return 'FIXED';
}

function clampSize(n: NodeData, axis: 'H' | 'V', v: number): number {
  const min = axis === 'H' ? n.minWidth : n.minHeight;
  const max = axis === 'H' ? n.maxWidth : n.maxHeight;
  if (max !== undefined && v > max) v = max;
  if (min !== undefined && v < min) v = min;
  return Math.max(0, v);
}

/** Size a child along an axis given an assigned length (for FILL / STRETCH). */
function resizeChild(ov: Overlay, child: NodeData, axis: 'H' | 'V', len: number, depth: number) {
  len = clampSize(child, axis, len);
  if (axis === 'H') {
    ov.set(child.id, { width: len });
    const c = ov.get(child.id);
    if (c.type === 'TEXT' && c.textAutoResize === 'WIDTH_AND_HEIGHT') ov.set(c.id, { textAutoResize: 'HEIGHT' });
    if (c.type === 'TEXT') fitText(ov, c.id);
  } else {
    ov.set(child.id, { height: len });
    const c = ov.get(child.id);
    if (c.type === 'TEXT' && c.textAutoResize !== 'NONE') ov.set(c.id, { textAutoResize: 'NONE' });
  }
  if (isAutoLayout(ov.get(child.id))) layoutFrame(ov, child.id, depth + 1, true);
}

function layoutFrame(ov: Overlay, id: NodeId, depth: number, fromParent = false): void {
  if (depth > 64) return;
  const f = ov.get(id);
  if (!isAutoLayout(f)) return;
  if (f.layoutMode === 'GRID') return layoutGrid(ov, id, depth, fromParent);
  const horizontal = f.layoutMode === 'HORIZONTAL';
  const pAxis: 'H' | 'V' = horizontal ? 'H' : 'V';
  const cAxis: 'H' | 'V' = horizontal ? 'V' : 'H';
  const [st, sr, sb, sl] = strokeInset(f);
  const pt = (f.paddingTop ?? 0) + st,
    pr = (f.paddingRight ?? 0) + sr,
    pb = (f.paddingBottom ?? 0) + sb,
    pl = (f.paddingLeft ?? 0) + sl;
  const gap = f.itemSpacing ?? 0;
  const pStart = horizontal ? pl : pt;
  const pEnd = horizontal ? pr : pb;
  const cStart = horizontal ? pt : pl;
  const cEnd = horizontal ? pb : pr;
  const P = (n: NodeData) => (horizontal ? n.width : n.height);
  const Cc = (n: NodeData) => (horizontal ? n.height : n.width);

  // First, lay out hugging child frames so their sizes are known.
  for (const c of ov.children(id)) {
    if (!inFlow(c)) continue;
    if (isAutoLayout(c) && !fromParent) layoutFrame(ov, c.id, depth + 1);
    if (c.type === 'TEXT') fitText(ov, c.id);
  }

  const frameP = sizing(f, pAxis);
  const frameC = sizing(f, cAxis);
  const innerP0 = P(f) - pStart - pEnd;
  const innerC0 = Cc(f) - cStart - cEnd;

  let kids = ov.children(id).filter(inFlow);

  // Wrap (horizontal only, like the reference behaviour).
  if (f.layoutWrap && horizontal && frameP !== 'HUG') {
    const rowGap = f.counterAxisSpacing ?? gap;
    const rows: NodeData[][] = [];
    let row: NodeData[] = [];
    let used = 0;
    for (const k of kids) {
      const w = P(k);
      if (row.length && used + gap + w > innerP0 + 0.01) {
        rows.push(row);
        row = [];
        used = 0;
      }
      used += (row.length ? gap : 0) + w;
      row.push(k);
    }
    if (row.length) rows.push(row);
    let y = cStart;
    let maxW = 0;
    for (const r of rows) {
      const rowH = Math.max(0, ...r.map(Cc));
      const total = r.reduce((s, k) => s + P(k), 0) + gap * Math.max(0, r.length - 1);
      maxW = Math.max(maxW, total);
      let x = pStart + alignOffset(f.primaryAxisAlign, innerP0 - total);
      const between = f.primaryAxisAlign === 'SPACE_BETWEEN' && r.length > 1 ? (innerP0 - total + gap * (r.length - 1)) / (r.length - 1) : gap;
      if (f.primaryAxisAlign === 'SPACE_BETWEEN') x = pStart;
      for (const k of r) {
        const cOff = counterOffset(f.counterAxisAlign, rowH - Cc(k));
        ov.set(k.id, { x, y: y + cOff });
        x += P(k) + between;
      }
      y += rowH + rowGap;
    }
    const contentH = rows.length ? y - rowGap - cStart : 0;
    if (frameC === 'HUG') ov.set(id, { height: clampSize(f, 'V', contentH + cStart + cEnd) });
    return;
  }

  // Counter-axis sizing: stretch / FILL children.
  let innerC = innerC0;
  if (frameC === 'HUG') {
    const maxC = kids.reduce((m, k) => Math.max(m, sizing(k, cAxis) === 'FILL' || f.counterAxisAlign === 'STRETCH' ? 0 : Cc(k)), 0);
    innerC = maxC;
  }
  for (const k of kids) {
    if (sizing(k, cAxis) === 'FILL' || f.counterAxisAlign === 'STRETCH') {
      if (Math.abs(Cc(k) - innerC) > 1e-6) resizeChild(ov, k, cAxis, innerC, depth);
    }
  }
  kids = ov.children(id).filter(inFlow);

  // Primary axis: distribute free space to FILL children.
  const fills = kids.filter((k) => sizing(k, pAxis) === 'FILL');
  const fixedSum = kids.filter((k) => sizing(k, pAxis) !== 'FILL').reduce((s, k) => s + P(k), 0);
  const gaps = gap * Math.max(0, kids.length - 1);
  if (fills.length && frameP !== 'HUG') {
    const each = Math.max(0, (innerP0 - fixedSum - (f.primaryAxisAlign === 'SPACE_BETWEEN' ? 0 : gaps)) / fills.length);
    for (const k of fills) if (Math.abs(P(k) - each) > 1e-6) resizeChild(ov, k, pAxis, each, depth);
    kids = ov.children(id).filter(inFlow);
  }
  // Counter sizes may have changed (text reflow after width change); recompute hug.
  if (frameC === 'HUG') {
    innerC = kids.reduce((m, k) => Math.max(m, Cc(k)), 0);
  }

  const total = kids.reduce((s, k) => s + P(k), 0) + gaps;
  let innerP = innerP0;
  if (frameP === 'HUG') innerP = total;

  // Position children.
  let cursor = pStart;
  let between = gap;
  if (f.primaryAxisAlign === 'SPACE_BETWEEN' && kids.length > 1) {
    between = (innerP - (total - gaps)) / (kids.length - 1);
  } else {
    cursor += alignOffset(f.primaryAxisAlign, innerP - total);
  }
  for (const k of kids) {
    const cOff = cStart + counterOffset(f.counterAxisAlign, innerC - Cc(k));
    // Children in auto layout are not rotated around their own center for placement purposes;
    // we place their unrotated box.
    if (horizontal) ov.set(k.id, { x: cursor, y: cOff });
    else ov.set(k.id, { x: cOff, y: cursor });
    cursor += P(k) + between;
  }

  // Frame hug sizing.
  const props: NodeProps = {};
  if (frameP === 'HUG') {
    const v = clampSize(f, pAxis, total + pStart + pEnd);
    if (horizontal) props.width = v;
    else props.height = v;
  }
  if (frameC === 'HUG') {
    const v = clampSize(f, cAxis, innerC + cStart + cEnd);
    if (horizontal) props.height = v;
    else props.width = v;
  }
  ov.set(id, props);
}

/** Where each grid child goes: explicit cells first, then row-major auto-placement into free cells. */
export function placeGridItems(cols: number, kids: NodeData[]): { node: NodeData; col: number; row: number; cs: number; rs: number }[] {
  const taken = new Set<string>();
  const out: { node: NodeData; col: number; row: number; cs: number; rs: number }[] = [];
  const free = (c: number, r: number, cs: number, rs: number) => {
    if (c + cs > cols) return false;
    for (let i = c; i < c + cs; i++) for (let j = r; j < r + rs; j++) if (taken.has(`${i},${j}`)) return false;
    return true;
  };
  const take = (c: number, r: number, cs: number, rs: number) => {
    for (let i = c; i < c + cs; i++) for (let j = r; j < r + rs; j++) taken.add(`${i},${j}`);
  };
  const span = (k: NodeData) => ({ cs: Math.max(1, Math.min(cols, k.gridColumnSpan ?? 1)), rs: Math.max(1, k.gridRowSpan ?? 1) });
  for (const k of kids) {
    if (k.gridColumn === undefined || k.gridRow === undefined) continue;
    const { cs, rs } = span(k);
    const col = Math.max(0, Math.min(cols - cs, k.gridColumn));
    take(col, k.gridRow, cs, rs);
    out.push({ node: k, col, row: Math.max(0, k.gridRow), cs, rs });
  }
  let cursor = 0;
  for (const k of kids) {
    if (k.gridColumn !== undefined && k.gridRow !== undefined) continue;
    const { cs, rs } = span(k);
    for (; ; cursor++) {
      const c = cursor % cols,
        r = Math.floor(cursor / cols);
      if (free(c, r, cs, rs)) {
        take(c, r, cs, rs);
        out.push({ node: k, col: c, row: r, cs, rs });
        cursor++;
        break;
      }
      if (cursor > 100000) break;
    }
  }
  return out;
}

/** Track sizes: fixed, then content (hug), then the free space shared by flex tracks. */
function sizeTracks(tracks: GridTrack[], inner: number | null, gap: number, items: { start: number; span: number; size: number; fill: boolean }[]): number[] {
  const sizes = tracks.map((t) => (t.type === 'FIXED' ? (t.value ?? 100) : 0));
  // content sizing from single-track items (spanning items only grow tracks if needed, below)
  for (const it of items) {
    if (it.span !== 1 || it.fill) continue;
    const t = tracks[it.start];
    if (t.type === 'HUG' || (t.type === 'FLEX' && inner === null)) sizes[it.start] = Math.max(sizes[it.start], it.size);
  }
  const gaps = gap * Math.max(0, tracks.length - 1);
  if (inner !== null) {
    const used = sizes.reduce((a, b, i) => a + (tracks[i].type === 'FLEX' ? 0 : b), 0);
    const frTotal = tracks.reduce((a, t) => a + (t.type === 'FLEX' ? (t.value ?? 1) : 0), 0);
    const freeSpace = Math.max(0, inner - used - gaps);
    tracks.forEach((t, i) => {
      if (t.type === 'FLEX' && frTotal > 0) sizes[i] = (freeSpace * (t.value ?? 1)) / frTotal;
    });
  }
  // spanning content that doesn't fit grows its last hug track
  for (const it of items) {
    if (it.span === 1 || it.fill) continue;
    const span = sizes.slice(it.start, it.start + it.span).reduce((a, b) => a + b, 0) + gap * (it.span - 1);
    const lastHug = [...Array(it.span).keys()].map((k) => it.start + k).reverse().find((i) => tracks[i]?.type === 'HUG');
    if (span < it.size && lastHug !== undefined) sizes[lastHug] += it.size - span;
  }
  return sizes;
}

function layoutGrid(ov: Overlay, id: NodeId, depth: number, fromParent: boolean) {
  const f = ov.get(id);
  for (const c of ov.children(id)) {
    if (!inFlow(c)) continue;
    if (isAutoLayout(c) && !fromParent) layoutFrame(ov, c.id, depth + 1);
    if (c.type === 'TEXT') fitText(ov, c.id);
  }
  const [st, sr, sb, sl] = strokeInset(f);
  const pt = (f.paddingTop ?? 0) + st,
    pr = (f.paddingRight ?? 0) + sr,
    pb = (f.paddingBottom ?? 0) + sb,
    pl = (f.paddingLeft ?? 0) + sl;
  const colGap = f.itemSpacing ?? 0;
  const rowGap = f.counterAxisSpacing ?? colGap;
  const colTracks: GridTrack[] = f.gridColumns?.length ? f.gridColumns : [{ type: 'FLEX', value: 1 }, { type: 'FLEX', value: 1 }];
  const kids = ov.children(id).filter(inFlow);
  const placed = placeGridItems(colTracks.length, kids);
  const rowsNeeded = placed.reduce((m, p) => Math.max(m, p.row + p.rs), 0);
  const rowTracks: GridTrack[] = [...(f.gridRows ?? [])];
  while (rowTracks.length < Math.max(1, rowsNeeded)) rowTracks.push({ type: 'HUG' }); // implicit rows hug content
  const hugW = sizing(f, 'H') === 'HUG';
  const hugH = sizing(f, 'V') === 'HUG';
  const colSizes = sizeTracks(colTracks, hugW ? null : f.width - pl - pr, colGap, placed.map((p) => ({ start: p.col, span: p.cs, size: p.node.width, fill: sizing(p.node, 'H') === 'FILL' })));
  const offs = (sizes: number[], gap: number, start: number) => sizes.map((_, i) => start + sizes.slice(0, i).reduce((a, b) => a + b, 0) + gap * i);
  const colX = offs(colSizes, colGap, pl);
  const cellW = (p: (typeof placed)[number]) => colSizes.slice(p.col, p.col + p.cs).reduce((a, b) => a + b, 0) + colGap * (p.cs - 1);
  // widths first (text reflows and grows taller), then rows from the new heights
  for (const p of placed) if (sizing(p.node, 'H') === 'FILL') resizeChild(ov, p.node, 'H', cellW(p), depth);
  const fresh = (p: (typeof placed)[number]) => ov.get(p.node.id);
  const rowSizes = sizeTracks(rowTracks, hugH ? null : f.height - pt - pb, rowGap, placed.map((p) => ({ start: p.row, span: p.rs, size: fresh(p).height, fill: sizing(p.node, 'V') === 'FILL' })));
  const rowY = offs(rowSizes, rowGap, pt);
  const cellH = (p: (typeof placed)[number]) => rowSizes.slice(p.row, p.row + p.rs).reduce((a, b) => a + b, 0) + rowGap * (p.rs - 1);
  for (const p of placed) if (sizing(p.node, 'V') === 'FILL') resizeChild(ov, p.node, 'V', cellH(p), depth);
  // alignment inside the cell: primary = horizontal, counter = vertical
  for (const p of placed) {
    const k = fresh(p);
    const x = colX[p.col] + alignOffset(f.primaryAxisAlign === 'SPACE_BETWEEN' ? 'MIN' : f.primaryAxisAlign, cellW(p) - k.width);
    const y = rowY[p.row] + counterOffset(f.counterAxisAlign, cellH(p) - k.height);
    ov.set(k.id, { x, y });
  }
  const props: NodeProps = {};
  const sum = (a: number[], g: number) => a.reduce((x, y) => x + y, 0) + g * Math.max(0, a.length - 1);
  if (hugW) props.width = clampSize(f, 'H', sum(colSizes, colGap) + pl + pr);
  if (hugH) props.height = clampSize(f, 'V', sum(rowSizes, rowGap) + pt + pb);
  if (Object.keys(props).length) ov.set(id, props);
}

function alignOffset(a: NodeData['primaryAxisAlign'], free: number): number {
  if (a === 'CENTER') return free / 2;
  if (a === 'MAX') return free;
  return 0;
}

function counterOffset(a: NodeData['counterAxisAlign'], free: number): number {
  if (a === 'CENTER') return free / 2;
  if (a === 'MAX') return free;
  return 0;
}

// ---------------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------------

function fitGroup(ov: Overlay, id: NodeId) {
  if (!ov.doc.has(id)) return;
  const g = ov.get(id);
  const kids = ov.children(id);
  if (!kids.length) return; // empty groups are removed by the editor
  let b: Rect | null = null;
  for (const k of kids) b = unionRect(b, transformRect(localTransform(k), { x: 0, y: 0, width: k.width, height: k.height }));
  if (!b) return;
  if (Math.abs(b.x) < 1e-6 && Math.abs(b.y) < 1e-6 && Math.abs(b.width - g.width) < 1e-6 && Math.abs(b.height - g.height) < 1e-6) return;
  // Keep children visually fixed: the new box's centre must land where the old transform
  // maps the children's bounds centre (rotation is about the centre, so this is exact).
  const oldM = localTransform(g);
  const worldCenter = applyMat(oldM, rectCenter(b));
  const nx = worldCenter.x - b.width / 2;
  const ny = worldCenter.y - b.height / 2;
  ov.set(id, { x: nx, y: ny, width: b.width, height: b.height });
  for (const k of kids) ov.set(k.id, { x: k.x - b.x, y: k.y - b.y });
}

// ---------------------------------------------------------------------------
// Connectors
// ---------------------------------------------------------------------------

export type Side = 'TOP' | 'RIGHT' | 'BOTTOM' | 'LEFT' | 'CENTER';

export interface ConnectorGeometry {
  /** Polyline / control points in page coordinates. For CURVED: [p0, c1, c2, p1]. */
  points: Vec2[];
  kind: 'STRAIGHT' | 'ELBOW' | 'CURVED';
  startSide: Side | null;
  endSide: Side | null;
}

type BoundsFn = (id: NodeId) => Rect | null;

function sidePoint(r: Rect, side: Side): Vec2 {
  switch (side) {
    case 'TOP':
      return { x: r.x + r.width / 2, y: r.y };
    case 'BOTTOM':
      return { x: r.x + r.width / 2, y: r.y + r.height };
    case 'LEFT':
      return { x: r.x, y: r.y + r.height / 2 };
    case 'RIGHT':
      return { x: r.x + r.width, y: r.y + r.height / 2 };
    default:
      return rectCenter(r);
  }
}

function autoSide(r: Rect, toward: Vec2): Side {
  const c = rectCenter(r);
  const dx = (toward.x - c.x) / Math.max(1, r.width);
  const dy = (toward.y - c.y) / Math.max(1, r.height);
  if (Math.abs(dx) > Math.abs(dy)) return dx > 0 ? 'RIGHT' : 'LEFT';
  return dy > 0 ? 'BOTTOM' : 'TOP';
}

const NORMAL: Record<Side, Vec2> = {
  TOP: { x: 0, y: -1 },
  BOTTOM: { x: 0, y: 1 },
  LEFT: { x: -1, y: 0 },
  RIGHT: { x: 1, y: 0 },
  CENTER: { x: 0, y: 0 },
};

export function connectorGeometryFrom(n: NodeData, bounds: BoundsFn): ConnectorGeometry {
  const s = n.connectorStart ?? { x: 0, y: 0 };
  const e = n.connectorEnd ?? { x: 100, y: 0 };
  const sr = s.nodeId ? bounds(s.nodeId) : null;
  const er = e.nodeId ? bounds(e.nodeId) : null;
  const sCenter = sr ? rectCenter(sr) : { x: s.x, y: s.y };
  const eCenter = er ? rectCenter(er) : { x: e.x, y: e.y };
  let sSide: Side | null = null;
  let eSide: Side | null = null;
  let p0: Vec2 = { x: s.x, y: s.y };
  let p1: Vec2 = { x: e.x, y: e.y };
  if (sr) {
    sSide = s.magnet && s.magnet !== 'AUTO' ? (s.magnet as Side) : autoSide(sr, eCenter);
    p0 = sidePoint(sr, sSide);
  }
  if (er) {
    eSide = e.magnet && e.magnet !== 'AUTO' ? (e.magnet as Side) : autoSide(er, sCenter);
    p1 = sidePoint(er, eSide);
  }
  const kind = n.connectorType ?? 'ELBOW';
  if (kind === 'STRAIGHT') return { points: [p0, p1], kind, startSide: sSide, endSide: eSide };
  if (kind === 'CURVED') {
    const d = Math.max(40, Math.hypot(p1.x - p0.x, p1.y - p0.y) * 0.4);
    const n0 = sSide ? NORMAL[sSide] : normalize({ x: p1.x - p0.x, y: p1.y - p0.y });
    const n1 = eSide ? NORMAL[eSide] : normalize({ x: p0.x - p1.x, y: p0.y - p1.y });
    return {
      points: [p0, { x: p0.x + n0.x * d, y: p0.y + n0.y * d }, { x: p1.x + n1.x * d, y: p1.y + n1.y * d }, p1],
      kind,
      startSide: sSide,
      endSide: eSide,
    };
  }
  return { points: elbowRoute(p0, sSide, p1, eSide), kind, startSide: sSide, endSide: eSide };
}

function normalize(v: Vec2): Vec2 {
  const l = Math.hypot(v.x, v.y) || 1;
  return { x: v.x / l, y: v.y / l };
}

function isHoriz(s: Side | null) {
  return s === 'LEFT' || s === 'RIGHT';
}

function elbowRoute(p0: Vec2, s0: Side | null, p1: Vec2, s1: Side | null): Vec2[] {
  const STUB = 24;
  const a = s0 && s0 !== 'CENTER' ? { x: p0.x + NORMAL[s0].x * STUB, y: p0.y + NORMAL[s0].y * STUB } : p0;
  const b = s1 && s1 !== 'CENTER' ? { x: p1.x + NORMAL[s1].x * STUB, y: p1.y + NORMAL[s1].y * STUB } : p1;
  const h0 = s0 ? isHoriz(s0) : Math.abs(p1.x - p0.x) >= Math.abs(p1.y - p0.y);
  const h1 = s1 ? isHoriz(s1) : h0;
  let mid: Vec2[];
  if (h0 && h1) {
    const mx = (a.x + b.x) / 2;
    mid = [{ x: mx, y: a.y }, { x: mx, y: b.y }];
  } else if (!h0 && !h1) {
    const my = (a.y + b.y) / 2;
    mid = [{ x: a.x, y: my }, { x: b.x, y: my }];
  } else if (h0) {
    mid = [{ x: b.x, y: a.y }];
  } else {
    mid = [{ x: a.x, y: b.y }];
  }
  const pts = [p0, a, ...mid, b, p1];
  // remove duplicates & collinear points
  const out: Vec2[] = [];
  for (const p of pts) {
    const last = out[out.length - 1];
    if (last && Math.abs(last.x - p.x) < 0.01 && Math.abs(last.y - p.y) < 0.01) continue;
    if (out.length >= 2) {
      const q = out[out.length - 2];
      const cross = (last.x - q.x) * (p.y - q.y) - (last.y - q.y) * (p.x - q.x);
      if (Math.abs(cross) < 0.01) {
        out[out.length - 1] = p;
        continue;
      }
    }
    out.push(p);
  }
  return out;
}

function updateConnectors(doc: Document, ov: Overlay, touched: Set<NodeId>) {
  // Collect connectors attached to touched nodes or their descendants/ancestors.
  const pagesTouched = new Set<NodeId>();
  for (const id of touched) {
    const p = doc.pageOf(id);
    if (p) pagesTouched.add(p.id);
  }
  if (!pagesTouched.size) return;
  const relevant = (id: NodeId | undefined) => {
    if (!id) return false;
    if (touched.has(id)) return true;
    for (const a of doc.ancestors(id)) if (touched.has(a.id)) return true;
    return false;
  };
  const boundsOf = (id: NodeId): Rect | null => {
    if (!doc.has(id)) return null;
    // Use overlay values for the node itself and ancestors.
    let m = localTransform(ov.get(id));
    let p = doc.get(ov.get(id).parent);
    while (p && p.type !== 'PAGE') {
      m = mulM(localTransform(ov.get(p.id)), m);
      p = doc.get(ov.get(p.id).parent);
    }
    const n = ov.get(id);
    return transformRect(m, { x: 0, y: 0, width: n.width, height: n.height });
  };
  for (const n of [...doc.connectors()]) {
    if (!pagesTouched.has(doc.pageOf(n.id)?.id ?? '')) continue;
    const c = ov.get(n.id);
    if (!touched.has(n.id) && !relevant(c.connectorStart?.nodeId) && !relevant(c.connectorEnd?.nodeId)) continue;
    const geo = connectorGeometryFrom(c, boundsOf);
    const pts = geo.points;
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const p of pts) {
      x0 = Math.min(x0, p.x); y0 = Math.min(y0, p.y); x1 = Math.max(x1, p.x); y1 = Math.max(y1, p.y);
    }
    const s = c.connectorStart!;
    const e = c.connectorEnd!;
    // Convert the page-space bbox into the connector parent's space.
    const parent = doc.get(c.parent);
    let origin = { x: x0, y: y0 };
    if (parent && parent.type !== 'PAGE') origin = applyMat(invert(doc.worldTransform(parent.id)), origin);
    ov.set(n.id, {
      connectorStart: { ...s, x: round(pts[0].x), y: round(pts[0].y) },
      connectorEnd: { ...e, x: round(pts[pts.length - 1].x), y: round(pts[pts.length - 1].y) },
      x: origin.x,
      y: origin.y,
      width: x1 - x0,
      height: y1 - y0,
      rotation: 0,
    });
  }
}



function mulM(m: ReturnType<typeof localTransform>, n: ReturnType<typeof localTransform>) {
  return [
    m[0] * n[0] + m[2] * n[1],
    m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3],
    m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4],
    m[1] * n[4] + m[3] * n[5] + m[5],
  ] as ReturnType<typeof localTransform>;
}

/** Connector geometry from the live document (used by renderers and hit testing). */
export function connectorGeometry(doc: Document, n: NodeData): ConnectorGeometry {
  return connectorGeometryFrom(n, (id) => (doc.has(id) ? doc.worldBounds(id) : null));
}

